import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ApiCatalogs, BatchFilter } from '@autopilot/contracts';
import { SELECTABLE_STATUSES, validateDateRange } from '../../lib/filters';
import { VORGANG_STATUS_LABEL } from '../../lib/labels';

export const UNTYPED_OPTION = { id: '__untyped', name: 'Ohne Typ', code: '' };

type Option = { id: string; name: string };

export function FilterCard({ value, onChange, catalogs, disabled }: {
  value: BatchFilter;
  onChange: (f: BatchFilter) => void;
  catalogs: ApiCatalogs;
  disabled?: boolean;
}) {
  const dateError = validateDateRange(value.createdFromDate, value.createdToDate);
  const typeOptions: Option[] = [UNTYPED_OPTION, ...catalogs.vorgangstypen];
  const selectedTypes: Option[] = [
    ...(value.includeUntyped ? [UNTYPED_OPTION] : []),
    ...catalogs.vorgangstypen.filter((t) => value.vorgangstypIds.includes(t.id)),
  ];
  const selectedG = catalogs.gesellschaften.filter((g) => value.gesellschaftIds.includes(g.id));
  const allStatus = value.statuses.length === SELECTABLE_STATUSES.length;

  return (
    <Card component="section" aria-labelledby="filter-title">
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Typography id="filter-title" variant="h5" component="h2" sx={{ mb: 0.5 }}>
          Ticketauswahl
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
          Leere Auswahl bedeutet jeweils alle Werte, für die Sie berechtigt sind. Gelöste und geschlossene Vorgänge werden nie berücksichtigt.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={catalogs.gesellschaften}
            value={selectedG}
            disabled={disabled}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => onChange({ ...value, gesellschaftIds: v.map((x) => x.id) })}
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props as typeof props & { key: string };
              return (
                <li key={key} {...rest}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} tabIndex={-1} />
                  {option.name}
                </li>
              );
            }}
            renderInput={(params) => <TextField {...params} label="Gesellschaften" placeholder={selectedG.length ? '' : 'Alle berechtigten Gesellschaften'} />}
            data-testid="filter-gesellschaften"
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={typeOptions}
            value={selectedTypes}
            disabled={disabled}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) =>
              onChange({
                ...value,
                includeUntyped: v.some((x) => x.id === UNTYPED_OPTION.id),
                vorgangstypIds: v.filter((x) => x.id !== UNTYPED_OPTION.id).map((x) => x.id),
              })
            }
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props as typeof props & { key: string };
              return (
                <li key={key} {...rest}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} tabIndex={-1} />
                  {option.id === UNTYPED_OPTION.id ? <em>Ohne Typ (nicht klassifiziert)</em> : option.name}
                </li>
              );
            }}
            renderInput={(params) => <TextField {...params} label="Vorgangstypen" placeholder={selectedTypes.length ? '' : 'Alle Typen inkl. „Ohne Typ“'} />}
            data-testid="filter-typen"
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              type="date"
              label="Erstellt von"
              value={value.createdFromDate ?? ''}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, createdFromDate: e.target.value || null })}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="einschließlich, Kalendertag (Berlin)"
              sx={{ flex: '1 1 180px' }}
            />
            <TextField
              type="date"
              label="Erstellt bis"
              value={value.createdToDate ?? ''}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, createdToDate: e.target.value || null })}
              slotProps={{ inputLabel: { shrink: true } }}
              error={!!dateError}
              helperText={dateError ?? 'einschließlich, Kalendertag (Berlin)'}
              sx={{ flex: '1 1 180px' }}
            />
          </Box>
          <FormControl component="fieldset" disabled={disabled}>
            <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
              Status
            </FormLabel>
            <FormGroup row sx={{ columnGap: 1 }}>
              {SELECTABLE_STATUSES.map((s) => (
                <FormControlLabel
                  key={s}
                  control={
                    <Checkbox
                      size="small"
                      checked={value.statuses.includes(s)}
                      onChange={(e) =>
                        onChange({
                          ...value,
                          statuses: e.target.checked ? SELECTABLE_STATUSES.filter((x) => x === s || value.statuses.includes(x)) : value.statuses.filter((x) => x !== s),
                        })
                      }
                    />
                  }
                  label={VORGANG_STATUS_LABEL[s]}
                />
              ))}
            </FormGroup>
            <FormHelperText sx={{ mx: 0 }}>
              {value.statuses.length === 0 ? 'Keine Auswahl = alle fünf aktiven Status.' : allStatus ? 'Alle aktiven Status ausgewählt.' : `${value.statuses.length} von 5 aktiven Status.`} „Gelöst“ und „Geschlossen“ sind nicht auswählbar.
            </FormHelperText>
          </FormControl>
          <FormControl component="fieldset" disabled={disabled} sx={{ gridColumn: { md: '1 / -1' } }}>
            <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
              Zuweisung
            </FormLabel>
            <RadioGroup row value={value.assignment} onChange={(e) => onChange({ ...value, assignment: e.target.value as BatchFilter['assignment'] })} sx={{ gap: 3 }}>
              <FormControlLabel
                value="ALL"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Alle Tickets</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Enthält auch bereits zugewiesene Vorgänge.</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="UNASSIGNED"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Nur unassigned</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Nur Vorgänge ohne verantwortliche Person.</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </CardContent>
    </Card>
  );
}
